import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.scss';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import dropThirdPartyEvents from 'helper/dropThirdPartyEvents';
import { persistor, store } from './redux/store';
import App from './App';
import reportWebVitals from './reportWebVitals';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [browserTracingIntegration()],

  // Sem `environment` explícito o SDK marca TODO evento como "production" por padrão —
  // erro de QA e erro de produção ficam indistinguíveis no Sentry.
  environment: process.env.REACT_APP_ENVIRONMENT || 'unknown',

  // Amarra o evento à versão publicada (a mesma que o app exibe). É o que permite dizer
  // "quebrou na v9.0.0" e, com upload de sourcemap, resolver o stack para o código real.
  release: process.env.REACT_APP_VERSION,

  // Descarta erro cujo stack é só de código de terceiro na aba do usuário (extensão de
  // navegador). Filtra por origem do frame, não por mensagem, para não silenciar um bug
  // nosso que produza o mesmo texto. Ver o helper para o caso que motivou isto.
  beforeSend: dropThirdPartyEvents,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

// O `import()` fica atrás das flags de mock. Sem nenhuma delas, o worker, o engine e as fixtures
// NÃO estão no `main.*.js`: ficam num chunk lazy que o build emite mas ninguém requisita. O
// webpack não elimina o ramo (as flags não estão no `.env`, então `process.env.X` é lido em
// tempo de execução); por isso o chunk existe em produção, só nunca é baixado.
async function prepareMocks() {
  if (
    process.env.REACT_APP_MOCK_METEOROLOGY_STATUS === 'true'
    || process.env.REACT_APP_MOCK_FLUX_PERSONALIZADO === 'true'
    || process.env.REACT_APP_MOCK_FLUX_UPLOAD === 'true'
  ) {
    const { default: worker } = await import('./mock/api/browser');
    // Com o mock do Flux ligado, o resto da API segue para o backend real sem poluir o console.
    await worker.start(
      process.env.REACT_APP_MOCK_FLUX_PERSONALIZADO === 'true'
      || process.env.REACT_APP_MOCK_FLUX_UPLOAD === 'true'
        ? { onUnhandledRequest: 'bypass' }
        : undefined,
    );
  }
}

prepareMocks().then(() => {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  );
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <App />
        </PersistGate>
      </Provider>
    </React.StrictMode>,
  );

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals();
});
